import type { ReactNode } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Overlay } from "@/components/flowly/infra/Overlay/Overlay";

interface ModalCardProps {
  isOpen: boolean;
  onClose: () => void;
  maxWidth?: string;
  className?: string;
  /**
   * When `false`, the modal cannot be closed by the user via the X button,
   * Escape key, or backdrop click — only by explicit action inside the
   * children (e.g. confirming a choice). Defaults to `true`. Use sparingly:
   * required-choice flows (marketing consent prompt, ToS acceptance) only.
   */
  dismissible?: boolean;
  children: ReactNode;
}

const NOOP = () => {};

export function ModalCard({
  isOpen,
  onClose,
  maxWidth = "max-w-md",
  className,
  dismissible = true,
  children,
}: ModalCardProps) {
  // When non-dismissible, pass a no-op to Overlay so Escape and backdrop
  // click do nothing. The X button is also omitted below.
  const overlayClose = dismissible ? onClose : NOOP;

  return (
    <Overlay isOpen={isOpen} onClose={overlayClose}>
      <div
        className={cn(
          "bg-card rounded-3xl p-8 border border-border shadow-2xl relative max-h-[90vh] overflow-y-auto w-full",
          maxWidth,
          className
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {dismissible && (
          <button
            onClick={onClose}
            className={cn(
              "absolute top-4 end-4 w-10 h-10 rounded-xl bg-secondary hover:bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
            )}
          >
            <X className="w-5 h-5" />
          </button>
        )}

        {children}
      </div>
    </Overlay>
  );
}
